/**
 * AG-UI SSE Streaming Client
 * Handles Server-Sent Events streaming from the AG-UI backend
 */

import type { AguiEvent } from '@/types/agui';
import { telemetryService } from '@/services/telemetryService';
import { getLogicEndpoint, apiVersions } from '@/services/endpoints';
import {
  getWorkflowAssistantAffinityHeader,
  captureWorkflowAssistantAffinity,
} from '@/services/workflowAssistantAffinity';

/**
 * Whether the dev script started a local assistant for this portal session.
 *
 * Set by `scripts/dev/dev.mjs` in two modes:
 *   - fully local (`npm run dev`)
 *   - hybrid (`npm run dev -- --cloud --assistant-local`)
 *
 * In any other deployment (canary, prod, plain `--cloud`) this is unset and
 * the portal continues to use the data-plane URL on the Otto control plane,
 * which proxies the request to the deployed assistant. `VITE_API_URL` alone
 * is NOT a sufficient signal — `.env.cloud`/`.env.canary` set it to the
 * deployed assistant URL for other purposes, so we need an explicit flag.
 *
 * Defined as a function (not a module-scoped constant) so unit tests can
 * vary `import.meta.env.VITE_ASSISTANT_LOCAL` between assertions via
 * `vi.stubEnv()` without needing to reset modules.
 */
function isAssistantLocal(): boolean {
  return import.meta.env.VITE_ASSISTANT_LOCAL === 'true';
}

/**
 * Resolve the override base URL for the local assistant, with defensive
 * trailing-slash trim. Returns `null` when we should fall back to the
 * data-plane URL (flag off, or override unset).
 *
 * Centralizing the lookup + trim avoids `…//api/agui/agent` outputs when a
 * `.env` carries `VITE_API_URL=http://localhost:3002/` (trailing slash).
 * Some proxies normalize the doubled slash, but the Kestrel server in the
 * assistant returns 404 for it — so we strip it up front.
 */
function getLocalAssistantBase(): string | null {
  if (!isAssistantLocal()) return null;
  const raw = import.meta.env.VITE_API_URL;
  if (!raw) return null;
  return raw.endsWith('/') ? raw.slice(0, -1) : raw;
}

/**
 * Resolve the URL to POST `invokeAgent` to.
 *
 * Production / cloud mode: portal calls the data-plane URL on the Otto control
 * plane (`{logicEndpoint}/automationProjects/{p}/workflowAssistants/default/invokeAgent`).
 * The control plane proxies the request to the deployed assistant's AgUiController.
 *
 * Local assistant mode (`VITE_ASSISTANT_LOCAL=true`): hit the assistant's
 * controller route (`/api/agui/agent`) directly. Skips the data-plane proxy
 * since there is no cloud control plane in front of the local assistant.
 *
 * Exported so unit tests can assert URL selection without running the full
 * `streamAguiMessage` flow.
 */
export function getInvokeAgentUrl(projectName: string): string {
  const base = getLocalAssistantBase();
  if (base) {
    return `${base}/api/agui/agent`;
  }
  return `${getLogicEndpoint()}/automationProjects/${encodeURIComponent(projectName)}/workflowAssistants/default/invokeAgent?api-version=${apiVersions.otto}`;
}

/**
 * Resolve the URL to POST tool-execution results to. Same local-assistant
 * reasoning as `getInvokeAgentUrl` — local assistant exposes
 * `/api/toolexecution/result` directly, cloud goes through the data-plane proxy.
 *
 * Exported for unit-test access (see `aguiStreamClient.test.ts`).
 */
export function getPostToolResultUrl(projectName: string): string {
  const base = getLocalAssistantBase();
  if (base) {
    return `${base}/api/toolexecution/result`;
  }
  return `${getLogicEndpoint()}/automationProjects/${encodeURIComponent(projectName)}/workflowAssistants/default/postToolExecutionResult?api-version=${apiVersions.otto}`;
}

/**
 * Resolve the URL to GET available LLM models from.
 *
 * In hybrid `--cloud --assistant-local` mode, the model picker should reflect
 * what the LOCAL assistant can serve (e.g. the single Foundry deployment named
 * in `AzureAI__ModelName`), not the cloud assistant's catalog. Without this
 * override, the picker would list cloud models (gpt-4o, etc.) and the user
 * would unknowingly pick a name that doesn't correspond to a deployment on
 * their Foundry resource — producing a `404 The API deployment for this
 * resource does not exist` when they send a prompt.
 */
export function getListModelsUrl(projectName: string): string {
  const base = getLocalAssistantBase();
  if (base) {
    return `${base}/api/agui/models`;
  }
  return `${getLogicEndpoint()}/automationProjects/${encodeURIComponent(projectName)}/workflowAssistants/default/listModels?api-version=${apiVersions.otto}`;
}

export interface MessageHistory {
  role: 'user' | 'assistant';
  content: string;
}

// Edit scope for scoped node editing (identified by jsonPath)
export interface EditScope {
  type: 'workflow' | 'node';
  nodes?: ScopedNode[];
}

export interface ScopedNode {
  name: string;
  jsonPath?: string;
}

export interface StreamOptions {
  prompt: string;
  projectName: string;
  accessToken: string;
  messages?: MessageHistory[];
  scope?: EditScope;
  workflowContext?: string;
  agentMode?: boolean;
  model?: string;
  sessionId?: string;
  /** Current connections.json (draft-aware) so the assistant can reuse existing connections. */
  connectionsJson?: string;
  signal?: AbortSignal;
  onEvent: (event: AguiEvent) => void;
  onError: (error: Error) => void;
}

// NOTE: System prompts are maintained in the backend only for security reasons.
// The frontend only passes the scope; the backend builds the appropriate prompt.

/**
 * Parse a single SSE line and dispatch to {@link onEvent} if it's a
 * `data:` frame carrying valid JSON. Non-data lines (comments, blank lines,
 * `event:` / `id:` headers we don't use) are intentionally ignored.
 *
 * Pulled out as a free function so it can be reused for both the
 * mid-stream chunked path and the final flush after stream end.
 */
function parseSseLine(line: string, onEvent: (event: AguiEvent) => void): void {
  if (!line.startsWith('data:')) {
    return;
  }
  const payload = line.substring(5).trim();
  if (payload.length === 0) {
    return;
  }
  try {
    const data = JSON.parse(payload) as AguiEvent;
    onEvent(data);
  } catch (e) {
    // A malformed frame should not corrupt the rest of the stream. The
    // previous implementation appended '}' to truncated JSON in an attempt
    // to recover, but that masked real bugs and could corrupt otherwise-
    // valid partial payloads. We now drop the frame and log it.
    console.error('[AG-UI] Failed to parse SSE event data:', e, payload.slice(0, 200));
  }
}

/**
 * Send a message to the AG-UI backend and stream the response
 */
export async function streamAguiMessage(options: StreamOptions): Promise<void> {
  const {
    prompt, projectName, accessToken, messages = [], scope, workflowContext,
    agentMode = true, model,
    sessionId, connectionsJson, signal, onEvent, onError,
  } = options;

  try {
    if (!accessToken) {
      throw new Error('Authentication token is missing. Please sign in and try again.');
    }

    if (!projectName.trim()) {
      throw new Error('Environment name is missing. Please select an environment and try again.');
    }

    const response = await fetch(getInvokeAgentUrl(projectName), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
        ...getWorkflowAssistantAffinityHeader(),
      },
      body: JSON.stringify({
        prompt,
        messages,
        agentMode,
        // Model selection
        ...(model ? { model } : {}),
        // Session ID for session reuse (avoids creating new CopilotSession per request)
        ...(sessionId ? { sessionId } : {}),
        // Scope is sent to backend which builds the appropriate system prompt
        ...(scope ? { scope } : {}),
        // Workflow context is sent for editing existing workflows
        ...(workflowContext ? { workflowContext } : {}),
        // Current connections so the assistant reuses existing connections instead of recreating them
        ...(connectionsJson ? { connectionsJson } : {})
      }),
      ...(signal ? { signal } : {})
    });

    // Pin all subsequent requests in this browser session to the same
    // backend replica using the affinity token the server returns here.
    captureWorkflowAssistantAffinity(response);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (!response.body) {
      throw new Error('No response body');
    }

    telemetryService.trackEvent('sse.agui.connected');

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        // Flush any residual bytes the TextDecoder might be holding (e.g. a
        // truncated UTF-8 continuation) and process any final complete event
        // still in the buffer before signaling stream end.
        buffer += decoder.decode();
        if (buffer.length > 0) {
          parseSseLine(buffer, onEvent);
          buffer = '';
        }
        telemetryService.trackEvent('sse.agui.disconnected', { reason: 'stream_complete' });
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      // SSE frame-safe split: parse every complete line (ending in \n) and
      // keep the final, possibly-incomplete fragment in the buffer for the
      // next read. Previously the code split on \n and then cleared the
      // entire buffer on every read, which dropped any trailing chunk that
      // happened to land mid-event. It also blindly appended '}' to truncated
      // JSON; both behaviors are removed because they corrupted otherwise-valid
      // streams once the new stage_event payloads (which routinely carry
      // multi-line `details`) started landing across chunk boundaries.
      let newlineIdx: number;
      while ((newlineIdx = buffer.indexOf('\n')) >= 0) {
        const line = buffer.substring(0, newlineIdx);
        buffer = buffer.substring(newlineIdx + 1);
        parseSseLine(line, onEvent);
      }
    }
  } catch (err: unknown) {
    if (err instanceof Error && err.name !== 'AbortError') {
      telemetryService.trackEvent('sse.agui.error', { error: err.message });
      telemetryService.trackEvent('sse.agui.disconnected', { reason: 'error' });
      onError(err);
    } else if (!(err instanceof Error)) {
      const errorMsg = String(err);
      telemetryService.trackEvent('sse.agui.error', { error: errorMsg });
      telemetryService.trackEvent('sse.agui.disconnected', { reason: 'error' });
      onError(new Error(errorMsg));
    } else {
      telemetryService.trackEvent('sse.agui.disconnected', { reason: 'aborted' });
    }
  }
}

/**
 * Post tool execution result back to the backend
 */
export async function postToolResult(projectName: string, accessToken: string, result: {
  execution_id: string;
  content: string;
  is_error: boolean;
}): Promise<void> {
  try {
    if (!accessToken) {
      console.error('[AG-UI] Cannot post tool result: missing accessToken');
      return;
    }

    if (!projectName) {
      console.error('[AG-UI] Cannot post tool result: missing projectName');
      return;
    }

    const response = await fetch(getPostToolResultUrl(projectName), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
        ...getWorkflowAssistantAffinityHeader(),
      },
      body: JSON.stringify(result)
    });

    captureWorkflowAssistantAffinity(response);

    if (!response.ok) {
      console.error('[AG-UI] Failed to post tool result:', response.statusText);
    }
  } catch (error) {
    console.error('[AG-UI] Error posting tool result:', error);
  }
}
